import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Stack,
  CircularProgress,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListSubheader,
  useMediaQuery,
  useTheme,
  Collapse,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Build as BuildIcon,
  Engineering as EngineeringIcon,
  Architecture as ArchitectureIcon,
  Sell as SellIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Category as CategoryIcon,
  Timeline as TimelineIcon,
  AdminPanelSettings as AdminPanelIcon,
  Check as CheckIcon,
  PlayArrow as PlayArrowIcon,
  ExpandMore as ExpandMoreIcon,
  FilterAlt as FilterAltIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import userService from "../services/userService";
import adminModuleService from "../services/adminModuleService";
import companyService from "../services/companyService";
import UserProgressDialog from "../components/UserProgressDialog";

function UserManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [roleAnchorEl, setRoleAnchorEl] = useState(null);
  const [onBoardingAnchorEl, setOnBoardingAnchorEl] = useState(null);
  const [completedAnchorEl, setCompletedAnchorEl] = useState(null);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Temporary states for mobile filter dialog (only apply when pressing Apply)
  const [tempRoleFilter, setTempRoleFilter] = useState("all");
  const [tempCompanyFilter, setTempCompanyFilter] = useState("all");
  const [tempOnBoardingFilter, setTempOnBoardingFilter] = useState("all");
  const [tempCompletedFilter, setTempCompletedFilter] = useState("all");

  const [companyFilter, setCompanyFilter] = useState("all");
  const [onBoardingFilter, setOnBoardingFilter] = useState("all");
  const [completedFilter, setCompletedFilter] = useState("all");
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [expandedUsers, setExpandedUsers] = useState({});

  const toggleUserExpand = (uid) => {
    setExpandedUsers((prev) => ({
      ...prev,
      [uid]: !prev[uid],
    }));
  };
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    installer: 0,
    engineer: 0,
    architect: 0,
    salesperson: 0,
  });

  const [formData, setFormData] = useState({
    displayName: "",
    email: "",
    role: "installer",
    parentCompany: "",
    password: "",
  });

  const [openCourseLockDialog, setOpenCourseLockDialog] = useState(false);
  const [selectedUserForCourses, setSelectedUserForCourses] = useState(null);
  const [courses, setCourses] = useState([]);
  const [userLockedCourses, setUserLockedCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [courseLockLoading] = useState({});
  const [savingLockedCourses, setSavingLockedCourses] = useState(false);

  // Progress management states
  const [openProgressDialog, setOpenProgressDialog] = useState(false);
  const [openOnboardingDialog, setOpenOnboardingDialog] = useState(false);
  const [selectedUserForProgress, setSelectedUserForProgress] = useState(null);
  const [userProgressDetail, setUserProgressDetail] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [companiesMap, setCompaniesMap] = useState({});

  const [companies, setCompanies] = useState([]);

  // NEW: Fetch companies for lookup
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await companyService.getAllCompanies();
        const companiesData = Array.isArray(response)
          ? response
          : response?.companies || [];

        setCompanies(companiesData);

        const map = {};
        companiesData.forEach((comp) => {
          map[comp.id] = comp.displayName || comp.name;
        });

        setCompaniesMap(map);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };
    fetchCompanies();
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        role: roleFilter !== "all" ? roleFilter : undefined,
        parentAccount: companyFilter !== "all" ? companyFilter : undefined,
        onBoarding:
          onBoardingFilter !== "all" ? onBoardingFilter === "true" : undefined,
        completed:
          completedFilter !== "all" ? completedFilter === "true" : undefined,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      const response = await userService.getAllUsers(params);

      setUsers(response.users || []);
      setFilteredUsers(response.users || []);
      setStats((prev) => response.stats || prev);
    } catch (error) {
      console.error("Error loading users:", error);
      setSnackbar({
        open: true,
        message: "Failed to load users. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    searchTerm,
    roleFilter,
    companyFilter,
    onBoardingFilter,
    completedFilter,
  ]);

  useEffect(() => {
    //Close filter dialog after select the filter
    handleCloseFilter();
  }, [companyFilter]);

  // Close other filters when they change
  useEffect(() => {
    handleCloseRoleFilter();
  }, [roleFilter]);

  useEffect(() => {
    handleCloseOnBoardingFilter();
  }, [onBoardingFilter]);

  useEffect(() => {
    handleCloseCompletedFilter();
  }, [completedFilter]);

  // Remove menu when in loading state
  useEffect(() => {
    setAnchorEl(null);
  }, [loading]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const clearFilters = () => {
    setSearchTerm("");
    setRoleFilter("all");
    setCompanyFilter("all");
    setOnBoardingFilter("all");
    setCompletedFilter("all");
    setPage(0);
    // Also clear temp filters
    setTempRoleFilter("all");
    setTempCompanyFilter("all");
    setTempOnBoardingFilter("all");
    setTempCompletedFilter("all");
  };

  // Mobile Filter Dialog handlers
  const handleOpenMobileFilter = () => {
    // Sync temp states with current filter states when opening
    setTempRoleFilter(roleFilter);
    setTempCompanyFilter(companyFilter);
    setTempOnBoardingFilter(onBoardingFilter);
    setTempCompletedFilter(completedFilter);
    setMobileFilterOpen(true);
  };

  const handleApplyMobileFilter = () => {
    // Apply temp states to actual filter states
    setRoleFilter(tempRoleFilter);
    setCompanyFilter(tempCompanyFilter);
    setOnBoardingFilter(tempOnBoardingFilter);
    setCompletedFilter(tempCompletedFilter);
    setPage(0);
    setMobileFilterOpen(false);
  };

  const handleClearMobileFilter = () => {
    setTempRoleFilter("all");
    setTempCompanyFilter("all");
    setTempOnBoardingFilter("all");
    setTempCompletedFilter("all");
  };

  const handleSelect = (uid) => {
    setSelected((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const handleSelectAll = (checked) => {
    setSelected(checked ? users.map((u) => u.uid) : []);
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        displayName: user.displayName || "",
        email: user.email || "",
        role: user.role || "installer",
        parentCompany: user.parentCompany || "",
        password: "", // Don't pre-fill password for editing
      });
    } else {
      setEditingUser(null);
      setFormData({
        displayName: "",
        email: "",
        role: "installer",
        parentCompany: "",
        password: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleSaveUser = async () => {
    try {
      setLoading(true);

      if (editingUser) {
        // Update existing user
        await userService.updateUser(editingUser.uid, {
          displayName: formData.displayName,
          email: formData.email,
          role: formData.role,
          parentCompany: formData.parentCompany,
        });
        setSnackbar({
          open: true,
          message: "User updated successfully!",
          severity: "success",
        });
      } else {
        // Add new user - password is required for new users
        if (!formData.password) {
          setSnackbar({
            open: true,
            message: "Password is required for new users",
            severity: "error",
          });
          return;
        }
        await userService.createUser(formData);
        setSnackbar({
          open: true,
          message: "User created successfully!",
          severity: "success",
        });
      }

      handleCloseDialog();
      loadUsers(); // Reload users after save
    } catch (error) {
      console.error("Save error:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          "Failed to save user. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMenu = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleOpenFilter = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
    setCompanySearchTerm("");
  };

  const handleOpenRoleFilter = (event) => {
    setRoleAnchorEl(event.currentTarget);
  };

  const handleCloseRoleFilter = () => {
    setRoleAnchorEl(null);
  };

  const handleOpenOnBoardingFilter = (event) => {
    setOnBoardingAnchorEl(event.currentTarget);
  };

  const handleCloseOnBoardingFilter = () => {
    setOnBoardingAnchorEl(null);
  };

  const handleOpenCompletedFilter = (event) => {
    setCompletedAnchorEl(event.currentTarget);
  };

  const handleCloseCompletedFilter = () => {
    setCompletedAnchorEl(null);
  };

  const exportUsers = () => {
    const csvContent = [
      ["UID", "Display Name", "Email", "Role", "Created At", "Updated At"],
      ...filteredUsers.map((user) => [
        user.uid,
        user.displayName || "",
        user.email || "",
        user.role || "",
        user.createdAt || "",
        user.updatedAt || "",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "installer":
        return "primary";
      case "engineer":
        return "warning";
      case "architect":
        return "info";
      case "salesperson":
        return "success";
      default:
        return "default";
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "installer":
        return <BuildIcon />;
      case "engineer":
        return <EngineeringIcon />;
      case "architect":
        return <ArchitectureIcon />;
      case "salesperson":
        return <SellIcon />;
      default:
        return <AdminPanelIcon />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  // NEW: Open delete confirmation dialog
  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
    handleCloseMenu(); // Close the action menu
  };

  // NEW: Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };

  // UPDATED: Confirm and execute delete
  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await userService.deleteUser(userToDelete.uid);
      setSnackbar({
        open: true,
        message: "User deleted successfully!",
        severity: "info",
      });
      handleCloseDeleteDialog();
      loadUsers(); // Reload users after delete
    } catch (error) {
      console.error("Delete error:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          "Failed to delete user. Please try again.",
        severity: "error",
      });
    }
  };

  // NEW: Load courses for locking
  const loadCourses = useCallback(async () => {
    setCoursesLoading(true);
    try {
      const response = await adminModuleService.getCourses();

      // Handle different response structures
      let coursesArray = [];
      if (response && response.data) {
        coursesArray = response.data;
      } else if (Array.isArray(response)) {
        coursesArray = response;
      } else {
        console.warn("Unexpected courses response structure:", response);
      }

      setCourses(coursesArray);
    } catch (error) {
      console.error("Error loading courses:", error);
      setSnackbar({
        open: true,
        message: "Failed to load courses. Please try again.",
        severity: "error",
      });
    } finally {
      setCoursesLoading(false);
    }
  }, []);

  // NEW: Open course lock dialog
  const handleOpenCourseLockDialog = (user) => {
    setSelectedUserForCourses(user);
    setOpenCourseLockDialog(true);
    handleCloseMenu(); // Close the action menu
  };

  // NEW: Close course lock dialog
  const handleCloseCourseLockDialog = () => {
    setOpenCourseLockDialog(false);
    setSelectedUserForCourses(null);
    setUserLockedCourses([]);
  };

  // NEW: Load user locked courses
  const loadUserLockedCourses = useCallback(async (userId) => {
    setCoursesLoading(true);
    try {
      // Use userService instead of adminModuleService for user-related operations
      const response = await userService.getUserLockedCourses(userId);
      // console.log('🔒 User locked courses response:', response); // Debug log

      // Handle different response structures
      let lockedCoursesArray = [];
      if (response && response.lockedCourses) {
        lockedCoursesArray = response.lockedCourses;
      } else if (response && response.courses) {
        lockedCoursesArray = response.courses;
      } else if (response && response.data && response.data.lockedCourses) {
        lockedCoursesArray = response.data.lockedCourses;
      } else if (response && response.data && response.data.courses) {
        lockedCoursesArray = response.data.courses;
      } else if (Array.isArray(response)) {
        lockedCoursesArray = response;
      } else {
        console.warn(
          "Unexpected Coming Soon courses response structure:",
          response
        );
      }

      // console.log('🔒 Final Coming Soon courses array:', lockedCoursesArray);
      setUserLockedCourses(lockedCoursesArray);
    } catch (error) {
      console.error("Error loading user Coming Soon courses:", error);
      setSnackbar({
        open: true,
        message: "Failed to load Coming Soon courses. Please try again.",
        severity: "error",
      });
    } finally {
      setCoursesLoading(false);
    }
  }, []);

  // NEW: Handle course lock/unlock
  const handleCourseLockToggle = (courseId) => {
    if (!selectedUserForCourses) return;
    setUserLockedCourses((prev) => {
      if (prev.includes(courseId)) {
        return prev.filter((id) => id !== courseId);
      } else {
        return [...prev, courseId];
      }
    });
  };

  // NEW: Load user locked courses when dialog opens
  useEffect(() => {
    if (openCourseLockDialog && selectedUserForCourses) {
      loadCourses();
      loadUserLockedCourses(selectedUserForCourses.uid);
    }
  }, [
    openCourseLockDialog,
    selectedUserForCourses,
    loadCourses,
    loadUserLockedCourses,
  ]);

  // NEW: Handle save locked courses
  const handleSaveLockedCourses = async () => {
    if (!selectedUserForCourses) return;
    setSavingLockedCourses(true);
    try {
      await adminModuleService.updateUserLockedCourses(
        selectedUserForCourses.uid,
        userLockedCourses
      );
      setSnackbar({
        open: true,
        message: "Coming Soon courses updated successfully!",
        severity: "success",
      });
      handleCloseCourseLockDialog();
      loadUsers();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update Coming Soon courses. Please try again.",
        severity: "error",
      });
    } finally {
      setSavingLockedCourses(false);
    }
  };

  // ===== PROGRESS MANAGEMENT HANDLERS =====

  // Open progress dialog
  const handleOpenProgressDialog = async (user) => {
    setSelectedUserForProgress(user);
    setOpenProgressDialog(true);
    handleCloseMenu(); // Close the action menu

    // Load user progress detail
    try {
      setProgressLoading(true);
      const progressDetail = await userService.getUserProgressDetail(user.uid);
      setUserProgressDetail(progressDetail);
    } catch (error) {
      console.error("Error loading user progress:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to load user progress",
        severity: "error",
      });
    } finally {
      setProgressLoading(false);
    }
  };

  // Close progress dialog
  const handleCloseProgressDialog = () => {
    setOpenProgressDialog(false);
    setSelectedUserForProgress(null);
    setUserProgressDetail(null);
  };

  // Adjust user progress
  const handleAdjustProgress = async (adjustData) => {
    try {
      const result = await userService.adjustUserProgress(adjustData);

      // Show success message with auto-completed info if applicable
      if (
        result &&
        result.autoCompletedModules &&
        result.autoCompletedModules.length > 0
      ) {
        setSnackbar({
          open: true,
          message: `User progress updated successfully! ${result.autoCompletedModules.length} previous module(s) were automatically completed.`,
          severity: "info",
        });
      } else {
        setSnackbar({
          open: true,
          message: "User progress updated successfully!",
          severity: "success",
        });
      }

      // Reload progress detail
      const progressDetail = await userService.getUserProgressDetail(
        adjustData.userId
      );
      setUserProgressDetail(progressDetail);

      // Return result for UserProgressDialog to handle
      return result;
    } catch (error) {
      console.error("Error adjusting user progress:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to adjust user progress",
        severity: "error",
      });
    }
  };

  // Reset user progress
  const handleResetProgress = async (resetData) => {
    try {
      await userService.resetUserProgress(resetData);

      setSnackbar({
        open: true,
        message: "User progress reset successfully!",
        severity: "success",
      });

      // Reload progress detail
      const progressDetail = await userService.getUserProgressDetail(
        resetData.userId
      );
      setUserProgressDetail(progressDetail);
    } catch (error) {
      console.error("Error resetting user progress:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to reset user progress",
        severity: "error",
      });
    }
  };

  const handleStartOnBoarding = () => {
    // Check if any selected user (in the current view) is missing an email
    const hasMissingEmail = users.some(
      (user) =>
        selected.includes(user.uid) && (!user.email || user.email.trim() === "")
    );

    if (hasMissingEmail) {
      setOpenOnboardingDialog(true);
    } else {
      handleConfirmOnBoarding();
    }
  };

  const handleConfirmOnBoarding = async () => {
    try {
      setLoading(true);
      await userService.startOnBoarding(selected);

      setSnackbar({
        open: true,
        message: "User start on boarding successfully!",
        severity: "success",
      });

      setSelected([]);
      loadUsers();
      setOpenOnboardingDialog(false);
    } catch (error) {
      setLoading(false);
      console.error("Error start onBoarding user:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to start onBoarding user",
        severity: "error",
      });
      setOpenOnboardingDialog(false);
    }
  };

  return (
    <Box sx={{ pb: { xs: 3, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: { xs: 2, md: 0 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", mb: 1, color: "primary.main" }}
          >
            User Management
          </Typography>
        </Box>

        {/* Actions Buttons - Desktop Only */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            width: { xs: "100%", md: "auto" },
            display: { xs: "none", md: "flex" },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadUsers}
            disabled={loading}
            sx={{ borderRadius: 2, bgcolor: "background.paper" }}
          >
            Refresh
          </Button>
          {/* <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportUsers}
            disabled={filteredUsers.length === 0}
            sx={{ borderRadius: 2, bgcolor: "background.paper" }}
          >
            Export
          </Button> */}
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleStartOnBoarding}
            disabled={filteredUsers.length === 0 || selected.length === 0}
            sx={{ borderRadius: 2 }}
          >
            Start Onboarding
          </Button>
        </Stack>
      </Box>

      {/* Mobile & Tablet Sticky Bottom Action Bar */}
      <Paper
        elevation={8}
        sx={{
          display: { xs: "flex", md: "none" },
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          p: 1.5,
          gap: 1,
          justifyContent: "center",
          alignItems: "center",
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          "& > button": {
            minWidth: { xs: "auto", sm: 120 },
          },
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadUsers}
          disabled={loading}
          sx={{ borderRadius: 2, flex: 1, maxWidth: 120 }}
        >
          Refresh
        </Button>
        {/* <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={exportUsers}
          disabled={filteredUsers.length === 0}
          sx={{ borderRadius: 2, flex: 1, maxWidth: 120 }}
        >
          Export
        </Button> */}
        <Button
          variant="contained"
          size="small"
          startIcon={<PlayArrowIcon />}
          onClick={handleStartOnBoarding}
          disabled={filteredUsers.length === 0 || selected.length === 0}
          sx={{ borderRadius: 2, flex: 1, maxWidth: 120 }}
        >
          Onboard
        </Button>
      </Paper>

      {/* Filters */}
      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
          borderRadius: 1,
          boxShadow: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterIcon color="primary" />
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold" }}
              color="text.primary"
            >
              Filters & Search
            </Typography>
          </Box>
        </Box>

        {/* Main Filters Row */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: { xs: 1, sm: 3 },
            alignItems: "center",
            mb: 3,
          }}
        >
          {/* Search Field */}
          <Box
            sx={{
              flex: { xs: "1 1 auto", sm: "1 1 40%", md: "1 1 50%" },
              minWidth: { xs: "auto", sm: "280px" },
            }}
          >
            <TextField
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="medium"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  backgroundColor: "action.hover",
                  height: 48,
                  "&:hover": {
                    backgroundColor: "action.selected",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "background.paper",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Mobile Filter Button */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Button
              variant="outlined"
              onClick={handleOpenMobileFilter}
              sx={{
                height: 48,
                minWidth: 48,
                width: 48,
                borderRadius: 1,
                bgcolor: "background.paper",
                borderColor: "divider",
                p: 0,
                color: "text.secondary",
              }}
            >
              <FilterAltIcon />
            </Button>
          </Box>

          {/* Pagination Controls Compact */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 30%", md: "1 1 25%" },
              display: { xs: "none", md: "flex" },
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "flex-start" },
              gap: { xs: 1, sm: 1.5 },
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
            {/* Per Page Selector */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: { xs: "space-between", sm: "flex-end" },
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontWeight: "medium",
                  whiteSpace: "nowrap",
                  fontSize: "0.875rem",
                }}
              >
                Per page
              </Typography>

              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: "background.paper",
                    height: 36,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "divider",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                    "& .MuiSelect-select": {
                      py: 0.5,
                      px: 1,
                      fontSize: "0.875rem",
                      fontWeight: "medium",
                      color: "text.primary",
                    },
                  }}
                  displayEmpty
                  renderValue={(value) => value}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", sm: "flex-end" },
              }}
            >
              {/* Navigation Controls */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  justifyContent: { xs: "center", sm: "flex-end" },
                }}
              >
                <Tooltip title="First page">
                  <span>
                    <IconButton
                      onClick={() => setPage(0)}
                      disabled={page === 0}
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <FirstPageIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Previous page">
                  <span>
                    <IconButton
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                    minWidth: { xs: 80, sm: 70 },
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      fontWeight: "medium",
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {page + 1} / {Math.ceil(stats.total / rowsPerPage) || 1}
                  </Typography>
                </Box>

                <Tooltip title="Next page">
                  <span>
                    <IconButton
                      onClick={() => setPage(page + 1)}
                      disabled={
                        page >= Math.ceil(stats.total / rowsPerPage) - 1
                      }
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Last page">
                  <span>
                    <IconButton
                      onClick={() =>
                        setPage(Math.ceil(stats.total / rowsPerPage) - 1)
                      }
                      disabled={
                        page >= Math.ceil(stats.total / rowsPerPage) - 1
                      }
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <LastPageIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontWeight: "medium",
                  fontSize: "0.875rem",
                  whiteSpace: "nowrap",
                  mt: 2,
                }}
              >
                Showing {Math.min(page * rowsPerPage + 1, stats.total)} -{" "}
                {Math.min((page + 1) * rowsPerPage, stats.total)} of{" "}
                {stats.total} users
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Active Filters Chips */}
        {(searchTerm ||
          roleFilter !== "all" ||
          companyFilter !== "all" ||
          onBoardingFilter !== "all" ||
          completedFilter !== "all") && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: { xs: 0.5, sm: 1 },
              mt: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mr: { xs: 0.5, sm: 1 },
                display: { xs: "none", sm: "block" },
              }}
            >
              Active Filters:
            </Typography>

            {searchTerm && (
              <Chip
                label={searchTerm}
                onDelete={() => setSearchTerm("")}
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  maxWidth: { xs: 120, sm: 200 },
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            )}

            {roleFilter !== "all" && (
              <Chip
                label={roleFilter.charAt(0).toUpperCase() + roleFilter.slice(1)}
                onDelete={() => setRoleFilter("all")}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}

            {companyFilter !== "all" && (
              <Chip
                label={
                  companies.find((c) => c.id === companyFilter)?.displayName ||
                  companies.find((c) => c.id === companyFilter)?.name ||
                  companyFilter
                }
                onDelete={() => setCompanyFilter("all")}
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  maxWidth: { xs: 100, sm: 180 },
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            )}

            {onBoardingFilter !== "all" && (
              <Chip
                label={
                  onBoardingFilter === "true" ? "Onboarded" : "Not Onboarded"
                }
                onDelete={() => setOnBoardingFilter("all")}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}

            {completedFilter !== "all" && (
              <Chip
                label={completedFilter === "true" ? "Completed" : "Incomplete"}
                onDelete={() => setCompletedFilter("all")}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}

            <Button
              size="small"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
              variant="outlined"
              color="warning"
              sx={{
                borderRadius: 2,
                whiteSpace: "nowrap",
                px: { xs: 1, sm: 2 },
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "warning.light",
                },
              }}
            >
              <Box sx={{ display: { xs: "none", sm: "block" } }}>CLEAR ALL</Box>
              <Box sx={{ display: { xs: "block", sm: "none" } }}>Clear</Box>
            </Button>
          </Box>
        )}

        {/* Mobile Pagination - Compact Single Row */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            justifyContent: "space-between",
            pt: 1,
            mt: 1,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FormControl size="small">
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{ height: 28, fontSize: "0.75rem", minWidth: 50 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
            <IconButton
              onClick={() => setPage(0)}
              disabled={page === 0}
              size="small"
              sx={{ p: 0.5 }}
            >
              <FirstPageIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              size="small"
              sx={{ p: 0.5 }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <Typography
              variant="caption"
              sx={{
                mx: 0.5,
                fontWeight: "medium",
                minWidth: 40,
                textAlign: "center",
              }}
            >
              {page + 1}/{Math.max(1, Math.ceil(stats.total / rowsPerPage))}
            </Typography>

            <IconButton
              onClick={() => setPage(page + 1)}
              disabled={page >= Math.ceil(stats.total / rowsPerPage) - 1}
              size="small"
              sx={{ p: 0.5 }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() =>
                setPage(Math.max(0, Math.ceil(stats.total / rowsPerPage) - 1))
              }
              disabled={page >= Math.ceil(stats.total / rowsPerPage) - 1}
              size="small"
              sx={{ p: 0.5 }}
            >
              <LastPageIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 60, textAlign: "right" }}
          >
            {stats.total} users
          </Typography>
        </Box>
      </Paper>

      {/* Users List - Mobile Card View OR Desktop Table View */}
      {isMobile ? (
        <Stack spacing={0}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography color="text.secondary">No users found</Typography>
            </Paper>
          ) : (
            filteredUsers.map((user) => (
              <Paper
                key={user.uid}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  position: "relative",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: 2,
                    borderColor: "primary.main",
                  },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                >
                  <Checkbox
                    checked={selected.includes(user.uid)}
                    onChange={() => handleSelect(user.uid)}
                    sx={{ p: 0.5, mt: -0.5, ml: -0.5 }}
                  />
                  <Box
                    sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                    onClick={() => toggleUserExpand(user.uid)}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" noWrap>
                      {user.displayName || "No Name"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <EmailIcon sx={{ fontSize: 14 }} /> {user.email}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mt: -0.5 }}>
                    <IconButton
                      onClick={() => toggleUserExpand(user.uid)}
                      size="small"
                      sx={{
                        transform: expandedUsers[user.uid]
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.3s",
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                    <IconButton
                      onClick={(e) => handleOpenMenu(e, user)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Collapse
                  in={expandedUsers[user.uid]}
                  timeout="auto"
                  unmountOnExit
                >
                  <Divider sx={{ my: 1.5 }} />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Role
                      </Typography>
                      <Chip
                        icon={getRoleIcon(user.role)}
                        label={
                          user.role?.charAt(0).toUpperCase() +
                          user.role?.slice(1)
                        }
                        color={getRoleColor(user.role)}
                        size="small"
                        variant="filled"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Company
                      </Typography>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ mt: 0.5, fontWeight: "medium" }}
                      >
                        {user.companyName ||
                          companiesMap[user.parentCompany] ||
                          "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Onboarding
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        {user.onBoarding ? (
                          <CheckIcon color="success" fontSize="small" />
                        ) : (
                          <ClearIcon color="error" fontSize="small" />
                        )}
                        <Typography variant="body2">
                          {user.onBoarding ? "Done" : "Pending"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Completed
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        {user.completed ? (
                          <CheckIcon color="success" fontSize="small" />
                        ) : (
                          <ClearIcon color="error" fontSize="small" />
                        )}
                        <Typography variant="body2">
                          {user.completed ? "Yes" : "No"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Paper>
            ))
          )}
        </Stack>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 1,
            boxShadow: 2,
            overflowX: "auto",
            "& .MuiTable-root": {
              minWidth: { xs: 800, md: "100%" },
            },
          }}
        >
          <Table sx={{ tableLayout: "auto", width: "100%" }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "action.hover" }}>
                <TableCell
                  align="center"
                  padding="checkbox"
                  sx={{ width: "50px" }}
                >
                  <Checkbox
                    // checked={allSelected}
                    // indeterminate={selected.length > 0 && !allSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    minWidth: 150,
                    whiteSpace: "nowrap",
                  }}
                >
                  User
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    minWidth: 180,
                    whiteSpace: "nowrap",
                  }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    minWidth: 160,
                    whiteSpace: "nowrap",
                  }}
                >
                  Parent Account
                  <IconButton size="small" onClick={handleOpenFilter}>
                    <FilterIcon />
                  </IconButton>
                  <Menu
                    anchorEl={filterAnchorEl}
                    open={Boolean(filterAnchorEl)}
                    onClose={handleCloseFilter}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    // Điểm bắt đầu của menu cũng ở phía trên bên phải để nó đổ xuống và sang trái
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    PaperProps={{ sx: { mt: 1, width: 250, padding: 2 } }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Filter by Account
                    </Typography>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    >
                      <InputLabel
                        id="company-select-label"
                        sx={{ color: "text.secondary" }}
                      >
                        Select Company
                      </InputLabel>
                      <Select
                        labelId="company-select-label"
                        value={companyFilter} // Giá trị này bây giờ sẽ là ID (hoặc "all")
                        label="Select Account"
                        onChange={(e) => setCompanyFilter(e.target.value)}
                        MenuProps={{ autoFocus: false }}
                        sx={{
                          // Tùy chỉnh để hợp với dark theme trong ảnh của bạn
                          bgcolor: "background.paper",
                          ".MuiOutlinedInput-notchedOutline": {
                            borderColor: "divider",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "primary.main",
                          },
                        }}
                      >
                        <ListSubheader>
                          <TextField
                            size="small"
                            autoFocus
                            placeholder="Search company..."
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                            onChange={(e) =>
                              setCompanySearchTerm(e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key !== "Escape") {
                                e.stopPropagation();
                              }
                            }}
                            value={companySearchTerm}
                          />
                        </ListSubheader>
                        <MenuItem value="all">
                          <em style={{ color: "gray" }}>All Account</em>
                        </MenuItem>

                        {companies
                          .filter((c) =>
                            (c.displayName || c.name || "")
                              .toLowerCase()
                              .includes(companySearchTerm.toLowerCase())
                          )
                          .map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {" "}
                              {/* Select lưu ID ở đây */}
                              {option.displayName || option.name}{" "}
                              {/* Nhưng hiển thị Name cho người dùng */}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => {
                          setCompanyFilter("all");
                          handleCloseFilter();
                        }}
                      >
                        Clear
                      </Button>
                      {/* <Button
                        size="small"
                        variant="contained"
                        onClick={handleCloseFilter}
                        sx={{ ml: 1 }}
                      >
                        Apply
                      </Button> */}
                    </Box>
                  </Menu>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    minWidth: 120,
                    whiteSpace: "nowrap",
                  }}
                >
                  Role
                  <IconButton size="small" onClick={handleOpenRoleFilter}>
                    <FilterIcon />
                  </IconButton>
                  <Menu
                    anchorEl={roleAnchorEl}
                    open={Boolean(roleAnchorEl)}
                    onClose={handleCloseRoleFilter}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem
                      onClick={() => setRoleFilter("all")}
                      selected={roleFilter === "all"}
                    >
                      All Roles
                    </MenuItem>
                    <MenuItem
                      onClick={() => setRoleFilter("installer")}
                      selected={roleFilter === "installer"}
                    >
                      Installer
                    </MenuItem>
                    <MenuItem
                      onClick={() => setRoleFilter("engineer")}
                      selected={roleFilter === "engineer"}
                    >
                      Engineer
                    </MenuItem>
                    <MenuItem
                      onClick={() => setRoleFilter("architect")}
                      selected={roleFilter === "architect"}
                    >
                      Architect
                    </MenuItem>
                    <MenuItem
                      onClick={() => setRoleFilter("salesperson")}
                      selected={roleFilter === "salesperson"}
                    >
                      Salesperson
                    </MenuItem>
                  </Menu>
                </TableCell>
                {/* <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                  Created At
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                  Updated At
                </TableCell> */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    minWidth: 100,
                    whiteSpace: "nowrap",
                  }}
                >
                  onBoarding
                  <IconButton size="small" onClick={handleOpenOnBoardingFilter}>
                    <FilterIcon />
                  </IconButton>
                  <Menu
                    anchorEl={onBoardingAnchorEl}
                    open={Boolean(onBoardingAnchorEl)}
                    onClose={handleCloseOnBoardingFilter}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem
                      onClick={() => setOnBoardingFilter("all")}
                      selected={onBoardingFilter === "all"}
                    >
                      All
                    </MenuItem>
                    <MenuItem
                      onClick={() => setOnBoardingFilter("true")}
                      selected={onBoardingFilter === "true"}
                    >
                      Completed
                    </MenuItem>
                    <MenuItem
                      onClick={() => setOnBoardingFilter("false")}
                      selected={onBoardingFilter === "false"}
                    >
                      Incomplete
                    </MenuItem>
                  </Menu>
                </TableCell>
                {/* <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", fontSize: "0.875rem" }}
                >
                  From Dynamic 365
                </TableCell> */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    minWidth: 100,
                    whiteSpace: "nowrap",
                  }}
                >
                  Completed
                  <IconButton size="small" onClick={handleOpenCompletedFilter}>
                    <FilterIcon />
                  </IconButton>
                  <Menu
                    anchorEl={completedAnchorEl}
                    open={Boolean(completedAnchorEl)}
                    onClose={handleCloseCompletedFilter}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem
                      onClick={() => setCompletedFilter("all")}
                      selected={completedFilter === "all"}
                    >
                      All
                    </MenuItem>
                    <MenuItem
                      onClick={() => setCompletedFilter("true")}
                      selected={completedFilter === "true"}
                    >
                      Completed
                    </MenuItem>
                    <MenuItem
                      onClick={() => setCompletedFilter("false")}
                      selected={completedFilter === "false"}
                    >
                      Incomplete
                    </MenuItem>
                  </Menu>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    minWidth: 80,
                    whiteSpace: "nowrap",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      Loading users...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} sx={{ textAlign: "center", py: 4 }}>
                    <PersonIcon
                      sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No users found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm || roleFilter !== "all"
                        ? "Try adjusting your search filters"
                        : "Start by adding your first user"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow
                    key={user.uid}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <TableCell align="center" padding="checkbox">
                      <Checkbox
                        checked={selected.includes(user.uid)}
                        onChange={() => handleSelect(user.uid)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: `${getRoleColor(user.role)}.main`,
                            color: `${getRoleColor(user.role)}.contrastText`,
                          }}
                        >
                          {(user.displayName || user.email || "U")
                            .charAt(0)
                            .toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "medium" }}
                            color="text.primary"
                          >
                            {user.displayName || "No Name"}
                          </Typography>
                          {/* <Typography variant="caption" color="text.secondary">
                            UID: {user.uid?.substring(0, 8)}...
                          </Typography> */}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <EmailIcon
                          sx={{ fontSize: 14, color: "text.secondary" }}
                        />
                        <Typography variant="body2" color="text.primary">
                          {user.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {user.companyName ||
                          companiesMap[user.parentCompany] ||
                          "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getRoleIcon(user.role)}
                        label={
                          user.role?.charAt(0).toUpperCase() +
                          user.role?.slice(1)
                        }
                        color={getRoleColor(user.role)}
                        size="small"
                        variant="filled"
                      />
                    </TableCell>
                    {/* <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {formatDate(user.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {formatDate(user.updatedAt)}
                      </Typography>
                    </TableCell> */}
                    <TableCell align="center">
                      <Typography variant="body2" color="text.primary">
                        {user.onBoarding ? (
                          <CheckIcon></CheckIcon>
                        ) : (
                          <ClearIcon></ClearIcon>
                        )}
                      </Typography>
                    </TableCell>
                    {/* <TableCell align="center">
                      <Typography variant="body2" color="text.primary">
                        {user.contactId === "" || !user.contactId ? (
                          <ClearIcon></ClearIcon>
                        ) : (
                          <CheckIcon></CheckIcon>
                        )}
                      </Typography>
                    </TableCell> */}
                    <TableCell align="center">
                      <Typography variant="body2" color="text.primary">
                        {user.completed ? (
                          <CheckIcon></CheckIcon>
                        ) : (
                          <ClearIcon></ClearIcon>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="More actions">
                        <IconButton
                          onClick={(e) => handleOpenMenu(e, user)}
                          size="small"
                          sx={{
                            "&:hover": {
                              backgroundColor: "action.hover",
                              color: "primary.main",
                            },
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {/* <MenuItem onClick={() => handleOpenDialog(selectedUser)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem> */}
        {/* <MenuItem onClick={() => console.log('View details', selectedUser)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem> */}
        {/* <MenuItem
          onClick={() => handleOpenDeleteDialog(selectedUser)}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem> */}
        <MenuItem
          onClick={() => handleOpenCourseLockDialog(selectedUser)}
          sx={{ color: "warning.main" }}
        >
          <ListItemIcon>
            <CategoryIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText>Lock Courses</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleOpenProgressDialog(selectedUser)}
          sx={{ color: "success.main" }}
        >
          <ListItemIcon>
            <TimelineIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText>Edit Progress</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add/Edit User Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
        {loading ? (
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column", // Or 'row' depending on your content layout
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading...
              </Typography>
            </Box>
          </DialogContent>
        ) : (
          <DialogContent>
            <Box
              sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}
            >
              <TextField
                label="Display Name"
                value={formData.displayName}
                onChange={(e) =>
                  setFormData({ ...formData, displayName: e.target.value })
                }
                fullWidth
                required
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                fullWidth
                required
              />
              {!editingUser && (
                <TextField
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  fullWidth
                  required
                  helperText="Minimum 6 characters"
                />
              )}
              <TextField
                select
                label="Role"
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
                fullWidth
                required
              >
                <MenuItem value="installer">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BuildIcon fontSize="small" />
                    Installer
                  </Box>
                </MenuItem>
                <MenuItem value="engineer">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EngineeringIcon fontSize="small" />
                    Engineer
                  </Box>
                </MenuItem>
                <MenuItem value="architect">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ArchitectureIcon fontSize="small" />
                    Architect
                  </Box>
                </MenuItem>
                <MenuItem value="salesperson">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SellIcon fontSize="small" />
                    Sales
                  </Box>
                </MenuItem>
              </TextField>

              <TextField
                select
                label="Parent Account"
                value={formData.parentCompany || ""}
                onChange={(e) =>
                  setFormData({ ...formData, parentCompany: e.target.value })
                }
                fullWidth
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.displayName || company.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            disabled={
              !formData.email ||
              !formData.displayName ||
              (!editingUser && !formData.password)
            }
          >
            {editingUser ? "Update User" : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-confirmation-dialog"
      >
        <DialogTitle id="delete-confirmation-dialog">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this user? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCourseLockDialog}
        onClose={handleCloseCourseLockDialog}
        aria-labelledby="course-lock-confirmation-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="course-lock-confirmation-dialog">
          {selectedUserForCourses
            ? `Lock Courses for ${selectedUserForCourses.displayName}`
            : "Lock Courses"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedUserForCourses
              ? `Manage course locks for ${selectedUserForCourses.displayName}`
              : "Select courses to lock or unlock"}
          </Typography>

          {/* Courses List */}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
            {coursesLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List>
                {courses.map((course) => {
                  const isComingSoon = userLockedCourses.includes(course.id);
                  return (
                    <ListItem key={course.id} disablePadding>
                      <ListItemText primary={course.name} />
                      <ListItemSecondaryAction>
                        {courseLockLoading[course.id] ? (
                          <CircularProgress size={24} />
                        ) : (
                          <IconButton
                            edge="end"
                            onClick={() => handleCourseLockToggle(course.id)}
                            size="small"
                            sx={{
                              color: isComingSoon
                                ? "error.main"
                                : "success.main",
                              "&:hover": {
                                backgroundColor: isComingSoon
                                  ? "error.lighter"
                                  : "success.lighter",
                              },
                            }}
                            disabled={!!courseLockLoading[course.id]}
                          >
                            {isComingSoon ? <LockIcon /> : <LockOpenIcon />}
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCourseLockDialog}>Cancel</Button>
          <Button
            onClick={handleSaveLockedCourses}
            variant="contained"
            disabled={savingLockedCourses}
            startIcon={
              savingLockedCourses ? <CircularProgress size={18} /> : null
            }
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Onboarding Confirmation Dialog */}
      <Dialog
        open={openOnboardingDialog}
        onClose={() => setOpenOnboardingDialog(false)}
        aria-labelledby="onboarding-confirmation-dialog"
      >
        <DialogTitle id="onboarding-confirmation-dialog">
          Confirm Start Onboarding
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            One or more selected user has email address missing. Please select
            provide an email address to continue
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenOnboardingDialog(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmOnBoarding}
            color="primary"
            variant="contained"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Progress Dialog */}
      <UserProgressDialog
        open={openProgressDialog}
        onClose={handleCloseProgressDialog}
        user={selectedUserForProgress}
        progressDetail={userProgressDetail}
        loading={progressLoading}
        onAdjustProgress={handleAdjustProgress}
        onResetProgress={handleResetProgress}
      />

      {/* Snackbar */}
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Mobile Filter Dialog */}
      <Dialog
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Filter Users
          <IconButton onClick={() => setMobileFilterOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Role Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={tempRoleFilter}
                label="Role"
                onChange={(e) => setTempRoleFilter(e.target.value)}
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="installer">Installer</MenuItem>
                <MenuItem value="engineer">Engineer</MenuItem>
                <MenuItem value="architect">Architect</MenuItem>
                <MenuItem value="salesperson">Salesperson</MenuItem>
              </Select>
            </FormControl>

            {/* Parent Account Filter */}
            <FormControl fullWidth size="small">
              <InputLabel id="mobile-company-select-label">
                Parent Account
              </InputLabel>
              <Select
                labelId="mobile-company-select-label"
                value={tempCompanyFilter}
                label="Parent Account"
                onChange={(e) => setTempCompanyFilter(e.target.value)}
                MenuProps={{ autoFocus: false }}
              >
                <ListSubheader>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Search company..."
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => setCompanySearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== "Escape") {
                        e.stopPropagation();
                      }
                    }}
                    value={companySearchTerm}
                  />
                </ListSubheader>
                <MenuItem value="all">
                  <em style={{ color: "gray" }}>All Accounts</em>
                </MenuItem>
                {companies
                  .filter((c) =>
                    (c.displayName || c.name || "")
                      .toLowerCase()
                      .includes(companySearchTerm.toLowerCase())
                  )
                  .map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.displayName || option.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* OnBoarding Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>OnBoarding</InputLabel>
              <Select
                value={tempOnBoardingFilter}
                label="OnBoarding"
                onChange={(e) => setTempOnBoardingFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Completed</MenuItem>
                <MenuItem value="false">Incomplete</MenuItem>
              </Select>
            </FormControl>

            {/* Completed Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Completed</InputLabel>
              <Select
                value={tempCompletedFilter}
                label="Completed"
                onChange={(e) => setTempCompletedFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearMobileFilter} color="warning">
            Clear All
          </Button>
          <Button onClick={handleApplyMobileFilter} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagement;
